/**
 * Record-type scope vocabulary shared by the field-config screens.
 *
 * Deliberately free of imports and side effects: the scope picker, the scope column and
 * the All Fields view all read from here, so they cannot drift apart on which record
 * types exist or what they are called.
 */

/** Which record type(s) a field is placed on.
 *
 *  Every one of these is honored by the backend: get_member_profile_fields filters on
 *  the per-org override COALESCEd over the field's default, and each has its own value
 *  store (members_forms, family_member_forms, household_forms, caregiver_forms,
 *  staff_forms). The UI previously only ever *assigned* member and contact, which is why
 *  family-member and household fields had to be created through the API. */
export type RecordTypeScope =
  | 'member'
  | 'contact'
  | 'family_member'
  | 'household'
  | 'caregiver'
  | 'staff'
  | 'activity_event';

/** Canonical labels + help text for the record types, so the scope picker, the scope
 *  column and the all-fields view can't drift apart on naming. Order is the order they
 *  are offered in the UI. */
export const RECORD_TYPE_SCOPES: {
  value: RecordTypeScope;
  label: string;
  hint: string;
  /** Whether a field can be assigned to this scope from the field-config screens.
   *  Staff fields are managed on the staff profile, not here. */
  assignable: boolean;
}[] = [
  { value: 'member', label: 'Members', hint: 'The member profile and the New Member form.', assignable: true },
  { value: 'contact', label: 'Contacts', hint: 'Contact records — the same table as members, split by record type.', assignable: true },
  {
    value: 'family_member',
    label: 'Family Members',
    hint: 'The Family Members tab, collected per family member.',
    assignable: true,
  },
  {
    value: 'household',
    label: 'Households',
    hint: 'The Household record — shared by everyone in the household, entered once.',
    assignable: true,
  },
  {
    value: 'caregiver',
    label: 'Caregivers',
    hint: 'A parent or legal guardian responsible for a member.',
    assignable: true,
  },
  {
    value: 'activity_event',
    label: 'Activity Events',
    hint: 'Anonymous activity capture (Outreach, Hub Meetings, Trainings).',
    assignable: true,
  },
  { value: 'staff', label: 'Staff', hint: 'Managed on the staff profile, not here.', assignable: false },
];

const RECORD_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  RECORD_TYPE_SCOPES.map((s) => [s.value, s.label])
);

/** Normalize a scope selection into the value the backend should persist.
 *
 *  The Field Config picker opens with Members + Contacts checked, which is the
 *  historical default. That default — and only that exact default — is stored as
 *  `null` (inherit) so the column is not filled with a redundant
 *  ['member','contact'].
 *
 *  TRUS-399: this must fold to null ONLY when the selection is EXACTLY
 *  {member, contact}. An earlier version folded whenever the selection merely
 *  *contained* both member and contact, so Members + Contacts + Households (and
 *  every other combination that kept both defaults checked and added a third
 *  scope) collapsed back to member+contact — the extra scopes were dropped on
 *  save with no error and reverted on reload. Any selection that is not exactly
 *  the default is persisted verbatim. */
export function normalizeAppliesToScope(
  scope?: RecordTypeScope[] | null
): RecordTypeScope[] | null {
  if (!scope || scope.length === 0) return null;
  const isExactlyMemberContact =
    scope.length === 2 && scope.includes('member') && scope.includes('contact');
  return isExactlyMemberContact ? null : scope;
}

/** Human-readable summary of a field's scope.
 *
 *  Replaces a version that only tested for member and contact and fell through to
 *  'Members + Contacts' for everything else — so an activity-event or staff field was
 *  labelled as if it applied to members, which is a wrong label rather than a missing
 *  one. Empty/null still means inherit, which really is members + contacts. */
export function recordTypeScopeLabel(scope?: RecordTypeScope[] | null): string {
  if (!scope || scope.length === 0) return 'Members + Contacts';
  const known = RECORD_TYPE_SCOPES.map((s) => s.value);
  const ordered = known.filter((v) => scope.includes(v));
  const unknown = scope.filter((v) => !known.includes(v));
  const labels = [...ordered.map((v) => RECORD_TYPE_LABELS[v]), ...unknown];
  if (labels.length === 0) return 'Members + Contacts';
  if (labels.length === 1) return `${labels[0]} only`;
  return labels.join(' + ');
}
